import Dialog from '$tablet-app/src/app/components/Dialog';
import { DialogProps } from '$tablet-app/src/app/components/Dialog/Dialog';
import DialogBottomActions from '$tablet-app/src/app/components/Dialog/DialogBottomActions';
import Spacer from '$tablet-app/src/app/components/Spacer';
import Text from '$tablet-app/src/app/components/Text';
import { OptionModel } from '$tablet-app/src/app/options/types';
import { useStateStore } from '$tablet-app/src/app/stores/useStateStore';
import { Button } from '@akin-home/buttons/native';
import { OptionType, filterOptionsByType } from '@akin-home/options';
import { useListOptions } from '@akin-home/options/react';
import { MEAL_TYPE_OPTIONS } from '@akin-home/recipes';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import React, { useMemo, useState } from 'react';
import styled from 'styled-components/native';

const FILTER_TITLES: Partial<Record<OptionType, string>> = {
  main_ingredient: 'Main Ingredient',
  dish_type: 'Dish Type',
  budget: 'Budget',
  preparation_time: 'Preparation Time',
  skill_level: 'Difficulty',
  special_diet: 'Dietary',
  allergy: 'Allergies',
};

/** This is for simulating meal types as options */
const MEAL_TYPE_FILTER_GROUP = {
  title: 'Meals',
  options: MEAL_TYPE_OPTIONS,
};

const RecipeFiltersDialog = (dialog_props: DialogProps) => {
  const { data: options } = useListOptions();

  const initial_selected_ids = useStateStore(
    (state) => state.recipe_search.active_filter_ids
  );

  const initial_excluded_ids = useStateStore(
    (state) => state.recipe_search.excluded_filter_ids
  );

  const closeDialog = useStateStore(
    (state) => state.recipe_search.closeFilterDialog
  );

  const setSelectedOptions = useStateStore(
    (state) => state.recipe_search.setActiveFilters
  );

  const setExcludeOptions = useStateStore(
    (state) => state.recipe_search.setExcludedFilters
  );

  const filterGroups = useMemo(() => {
    return [
      MEAL_TYPE_FILTER_GROUP,
      ...Object.keys(FILTER_TITLES).map((group) => ({
        title: FILTER_TITLES[group as OptionType],
        options: filterOptionsByType(group as OptionType)(options),
      })),
    ];
  }, [options]);

  const [selectedIDs, setSelectedIDs] =
    useState<string[]>(initial_selected_ids);

  const [excludedIDs, setExcludedIDs] =
    useState<string[]>(initial_excluded_ids);

  const handlePressDone = () => {
    setSelectedOptions(selectedIDs);
    setExcludeOptions(excludedIDs);
    closeDialog();
  };

  const onPressOption = (option: OptionModel) => () => {
    if (selectedIDs.includes(option.id)) {
      setSelectedIDs(selectedIDs.filter((id) => id !== option.id));
      setExcludedIDs([...excludedIDs, option.id]);
    } else if (excludedIDs.includes(option.id)) {
      setExcludedIDs(excludedIDs.filter((id) => id !== option.id));
    } else {
      setSelectedIDs([...selectedIDs, option.id]);
    }
  };

  return (
    <StyledDialog {...dialog_props} onClose={closeDialog}>
      <Heading variant="h3" weight="bold">
        Filter by
      </Heading>

      <SubHeading>Select as many as you need</SubHeading>

      <DialogContent>
        {filterGroups.map((group) => (
          <React.Fragment key={group.title}>
            <FilterGroupContainer>
              <Text variant="h3" weight="semibold">
                {group.title}
              </Text>

              <FilterGroup>
                {group.options.map((option) => (
                  <FilterPill
                    key={option.id}
                    active={selectedIDs.includes(option.id)}
                    excl={excludedIDs.includes(option.id)}
                    onPress={onPressOption}
                    option={option}
                  />
                ))}
              </FilterGroup>
            </FilterGroupContainer>
            <Spacer height="1px" color="lightgrey" />
          </React.Fragment>
        ))}
      </DialogContent>

      <BottomButtons>
        <Button variant="text" onPress={closeDialog}>
          Back
        </Button>

        <Button onPress={handlePressDone}>
          {`Apply ${selectedIDs.length + excludedIDs.length} filters`}
        </Button>
      </BottomButtons>
    </StyledDialog>
  );
};

interface FilterPillProps {
  active?: boolean;
  excl?: boolean;
  option: OptionModel;

  onPress: (option: OptionModel) => () => void;
}

const FilterPill = ({ active, excl, option, onPress }: FilterPillProps) => (
  <FilterButton active={active} excl={excl} onPress={onPress(option)}>
    {active ? (
      <FontAwesomeIcon icon="check-circle" color="#535AF1" />
    ) : excl ? (
      <FontAwesomeIcon icon="ban" color="red" />
    ) : (
      <FontAwesomeIcon icon={['far', 'circle']} />
    )}

    <FilterButtonText
      active={active}
      excl={excl}
      variant="p2"
      weight="semibold"
    >
      {option.label}
    </FilterButtonText>
  </FilterButton>
);

const StyledDialog = styled(Dialog)`
  max-width: 700px;
  width: 90%;
`;

const Heading = styled(Text)`
  padding: 16px 0 8px;
  text-align: center;
`;

const SubHeading = styled(Text)`
  text-align: center;
`;

const DialogContent = styled.ScrollView``;

const BottomButtons = styled(DialogBottomActions)`
  gap: 8px;
  align-items: center;
`;

const FilterGroupContainer = styled.View`
  padding: 16px;
`;

const FilterGroup = styled.View`
  flex-direction: row;
  flex-wrap: wrap;
  padding: 8px 0;
  gap: 8px;
`;

const FilterButton = styled.TouchableOpacity<{
  active?: boolean;
  excl?: boolean;
}>`
  flex-direction: row;
  align-items: center;
  padding: 8px 14px;
  background-color: ${({ active, excl }) =>
    active
      ? 'rgba(83, 90, 241, 0.1);'
      : excl
      ? 'rgba(241, 90, 83, 0.1);'
      : 'rgba(0, 0, 0, 0.1)'};
  border-radius: 20px;
  margin-bottom: 8px;

  gap: 8px;

  border: 2px solid
    ${({ active, excl }) =>
      active ? '#535AF1' : excl ? 'red' : 'rgba(0, 0, 0, 0.1)'};
`;

const FilterButtonText = styled(Text)<{ active?: boolean; excl?: boolean }>`
  ${({ active }) => (active ? 'color: #535AF1;' : '')}
`;

export default RecipeFiltersDialog;
